import { useState, type FormEvent } from "react";
import { createEmployeeNoteSchema, fieldErrorsFromZod } from "@evolua/schemas";
import { Button, Field } from "@evolua/ui";

export type EmployeeNoteFormProps = {
  employeeLabel: string;
  onSubmit: (text: string) => Promise<void>;
  disabled?: boolean;
};

/** Formulário de referência — validação via @evolua/schemas (mesmo schema da API). */
export function EmployeeNoteForm({
  employeeLabel,
  onSubmit,
  disabled,
}: EmployeeNoteFormProps) {
  const [text, setText] = useState("");
  const [fieldError, setFieldError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [ok, setOk] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setOk(false);
    setFormError(null);
    const parsed = createEmployeeNoteSchema.safeParse({ text });
    if (!parsed.success) {
      const fields = fieldErrorsFromZod(parsed.error);
      setFieldError(fields.text);
      return;
    }
    setFieldError(undefined);
    setPending(true);
    try {
      await onSubmit(parsed.data.text);
      setText("");
      setOk(true);
    } catch {
      setFormError("Não foi possível salvar a nota.");
    } finally {
      setPending(false);
    }
  }

  return (
    <form onSubmit={(e) => void handleSubmit(e)} noValidate>
      <p className="evolua-page-lead">
        Nota para <strong>{employeeLabel}</strong>
      </p>
      <Field
        label="Texto"
        hint="1 a 500 caracteres (schema compartilhado)"
        value={text}
        onChange={(ev) => setText(ev.target.value)}
        {...(fieldError ? { error: fieldError } : {})}
        disabled={disabled || pending}
      />
      {formError ? (
        <p role="alert" className="evolua-page-lead">
          {formError}
        </p>
      ) : null}
      {ok ? (
        <p className="evolua-page-lead" role="status">
          Nota registrada.
        </p>
      ) : null}
      <Button type="submit" disabled={disabled || pending}>
        {pending ? "Salvando…" : "Salvar nota"}
      </Button>
    </form>
  );
}
